// N6 — Northern Lights-sannsynlighet
// Henter Kp-indeks (planetary K-index) fra NOAA Space Weather Prediction Center.
// Kp 0-2: lav synlighet selv i Alaska. Kp 3-4: synlig over Juneau/Skagway hvis
// mørkt og klart. Kp 5+: storm, synlig sør for Alaska også.
//
// Reisen treffer Alaska 21-25. juli — sommer = lite mørke timer, så aurora er
// usannsynlig før sent på kvelden. Likevel interessant for brukerne hvis det
// kommer storm.

import { fetchWithTimeout } from './utils.js';

const NOAA_KP_URL = 'https://services.swpc.noaa.gov/products/noaa-planetary-k-index.json';

let cache = { ts: 0, data: null };

export async function fetchKpForecast(){
  // Cache 30 min
  if(cache.data && Date.now() - cache.ts < 30 * 60 * 1000) return cache.data;
  try {
    const r = await fetchWithTimeout(NOAA_KP_URL);
    if(!r.ok) throw new Error('NOAA ' + r.status);
    const rows = await r.json();
    // Første rad er headers: ["time_tag","Kp","a_running","station_count"]
    const data = rows.slice(1)
      .map(row => ({ time: new Date(row[0]), kp: row[1] == null ? null : Number(row[1]) }))
      .filter(d => Number.isFinite(d.kp) && d.time && !isNaN(d.time.getTime()));
    cache = { ts: Date.now(), data };
    return data;
  } catch (e){
    console.warn('NOAA fetch feilet', e);
    return null;
  }
}

// Beregn maks Kp for hver av Alaska-nettene (21-25 juli 2026)
const ALASKA_NIGHTS = [
  { date: '2026-07-21', label: 'Icy Strait Point' },
  { date: '2026-07-22', label: 'Skagway' },
  { date: '2026-07-23', label: 'Juneau' },
  { date: '2026-07-24', label: 'Ketchikan' },
  { date: '2026-07-25', label: 'Inside Passage' },
];

function kpVisibility(kp){
  if(kp < 3) return { label: 'Lite synlig', color: 'rgba(14,34,56,.55)', glyph: '◌' };
  if(kp < 5) return { label: 'Mulig', color: '#C99A3B', glyph: '◐' };
  if(kp < 7) return { label: 'Sannsynlig', color: '#2E6B5C', glyph: '◑' };
  return { label: 'Storm — gå på dekk!', color: '#B84A2E', glyph: '●' };
}

export async function getAuroraOutlook(){
  const forecast = await fetchKpForecast();
  if(!forecast || forecast.length === 0){
    return {
      mode: 'unavailable',
      kp: null,
      visibility: { label: 'Data utilgjengelig', color: 'rgba(14,34,56,.55)', glyph: '◌' },
      note: 'NOAA-feed kunne ikke leses akkurat nå.',
    };
  }

  // Hvis vi er på reisen 21-25 juli, finn nattens Kp
  const now = new Date();
  const dep = new Date('2026-07-21T00:00:00');
  const ret = new Date('2026-07-26T00:00:00');

  if(now < dep || now > ret){
    const recent = forecast.slice(-8);
    if(recent.length === 0){
      return {
        mode: 'unavailable',
        kp: null,
        visibility: { label: 'Data utilgjengelig', color: 'rgba(14,34,56,.55)', glyph: '◌' },
      };
    }
    const maxKp = Math.max(...recent.map(d => d.kp));
    return {
      mode: 'current',
      kp: maxKp,
      visibility: kpVisibility(maxKp),
      next24Max: maxKp,
      note: 'Sanntidsverdi. Dere er ikke i Alaska ennå.',
    };
  }

  // På reisen — sjekk hver natt
  const todayMidnight = new Date(now);
  todayMidnight.setHours(0,0,0,0);
  const tomorrowMidnight = new Date(todayMidnight);
  tomorrowMidnight.setDate(tomorrowMidnight.getDate() + 1);

  const tonightForecast = forecast.filter(d =>
    d.time >= todayMidnight && d.time < tomorrowMidnight && d.time.getHours() >= 21
  );
  const maxKp = tonightForecast.length ? Math.max(...tonightForecast.map(d => d.kp)) : 0;

  const tonightLabel = ALASKA_NIGHTS.find(n => {
    const nd = new Date(n.date + 'T00:00:00');
    return nd.toDateString() === todayMidnight.toDateString();
  });

  return {
    mode: 'tonight',
    kp: maxKp,
    visibility: kpVisibility(maxKp),
    location: tonightLabel?.label,
    note: 'Best mørke 23–02. Husk: sommer-Alaska = lite ekte mørke selv om midnatt.',
  };
}
